/**
 * Single source of truth for job origins — shared by the filter UI (per-source
 * toggles), the job-card badge, and the matchers. Add a new feed source here in
 * one place and it shows up everywhere.
 *
 * Only Sources actually wired to fetch are listed — the toggle, badge, and
 * matchers should never advertise an origin that can't deliver jobs. The ones
 * intentionally commented out below currently ingest nothing:
 *   - justremote  — reclassified Tier C (live listings went JS-rendered); pulled
 *     from runner/adapters/index.js until headless/proxy infra exists.
 *   - solidgigs / lemonio / codeable / scalablepath — Tier-D credential-gated;
 *     a clean zero-job no-op unless the operator sets the RUNNER_CRED_* secrets.
 * Re-enable one by uncommenting its row here once its Adapter is back in the
 * runner registry (or its credentials are provided).
 *
 * Upwork is now toggleable like every other origin: the scraper honours
 * `sources.upwork === false` (src/worker/scraper.js), so unchecking it stops
 * Upwork jobs from matching that filter instead of being a no-op switch.
 */

export const JOB_SOURCES = [
  { id: 'upwork', label: 'Upwork' },
  { id: 'remoteok', label: 'RemoteOK' },
  { id: 'workingnomads', label: 'Working Nomads' },
  { id: 'jobspresso', label: 'Jobspresso' },
  { id: 'dribbble', label: 'Dribbble' },
  { id: 'webflow', label: 'Webflow.jobs' },
  { id: 'onlinejobs', label: 'OnlineJobs.ph' },
  { id: 'flowremote', label: 'FlowRemote' },
  { id: 'remotehabits', label: 'RemoteHabits' },
  // Not fetching — see the note above. Uncomment to re-enable once wired.
  // { id: 'justremote', label: 'JustRemote' },
  // { id: 'solidgigs', label: 'SolidGigs' },
  // { id: 'lemonio', label: 'lemon.io' },
  // { id: 'codeable', label: 'Codeable' },
  // { id: 'scalablepath', label: 'ScalablePath' },
];

export const SOURCE_LABEL = Object.fromEntries(JOB_SOURCES.map((s) => [s.id, s.label]));

// Tailwind chip classes per origin so the job-card badge is scannable at a
// glance. Unknown/legacy rows fall back to the Upwork (neutral) style.
export const SOURCE_BADGE_CLASS = {
  upwork: 'bg-gray-100 text-gray-600',
  remoteok: 'bg-violet-100 text-violet-700',
  workingnomads: 'bg-amber-100 text-amber-700',
  jobspresso: 'bg-emerald-100 text-emerald-700',
  dribbble: 'bg-pink-100 text-pink-700',
  webflow: 'bg-blue-100 text-blue-700',
  onlinejobs: 'bg-teal-100 text-teal-700',
  flowremote: 'bg-orange-100 text-orange-700',
  remotehabits: 'bg-rose-100 text-rose-700',
  justremote: 'bg-cyan-100 text-cyan-700',
  solidgigs: 'bg-indigo-100 text-indigo-700',
  lemonio: 'bg-lime-100 text-lime-700',
  codeable: 'bg-fuchsia-100 text-fuchsia-700',
  scalablepath: 'bg-sky-100 text-sky-700',
};

/**
 * Is `source` enabled for a filter's criteria?
 *  - no `criteria.sources` map at all  → enabled (backward compat: pre-existing
 *    saved filters created before this feature keep matching everything)
 *  - explicit `false`                  → disabled
 *  - anything else (true / undefined)  → enabled
 */
export function isSourceEnabled(criteria, source) {
  const map = criteria && criteria.sources;
  if (!map || typeof map !== 'object') return true;
  return map[source || 'upwork'] !== false;
}
